import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import UrlCard from './components/UrlCard';
import AddUrlForm from './components/AddUrlForm';
import SearchBar from './components/SearchBar';
import TagFilter from './components/TagFilter';
import Auth from './components/Auth';
import TagManagement from './components/TagManagement';

function App() {
  const [urls, setUrls] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [showTagManagement, setShowTagManagement] = useState(false);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const savedUsername = localStorage.getItem('username');
    
    if (token && savedUsername) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      setUsername(savedUsername);
      fetchUrls();
      fetchTags();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, username) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
    setUsername(username);
    fetchUrls();
    fetchTags();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUsername('');
    setUrls([]);
    setTags([]);
  };

  const fetchUrls = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/urls');
      setUrls(response.data);
    } catch (error) {
      console.error('Error fetching URLs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get('/api/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleSearch = useCallback(async (query, tag, starred = false) => {
    try {
      setLoading(true);
      if (!query && !tag && !starred) {
        const response = await axios.get('/api/urls');
        setUrls(response.data);
        return;
      }
      
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (tag) params.append('tag', tag);
      if (starred) params.append('starred', 'true');
      
      const response = await axios.get(`/api/urls/search?${params}`);
      setUrls(response.data);
    } catch (error) {
      console.error('Error searching URLs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddUrl = async (urlData) => {
    try {
      await axios.post('/api/urls', urlData);
      await fetchUrls();
      await fetchTags();
    } catch (error) {
      console.error('Error adding URL:', error);
      throw error;
    }
  };

  const handleDeleteUrl = async (id) => {
    try {
      await axios.delete(`/api/urls/${id}`);
      await fetchUrls();
      await fetchTags();
    } catch (error) {
      console.error('Error deleting URL:', error);
    }
  };

  const handleUpdateTags = async (id, newTags) => {
    try {
      await axios.put(`/api/urls/${id}/tags`, { tags: newTags });
      await fetchUrls();
      await fetchTags();
    } catch (error) {
      console.error('Error updating tags:', error);
    }
  };

  const handleUpdateDescription = async (id, description) => {
    try {
      await axios.put(`/api/urls/${id}/description`, { description });
      await fetchUrls();
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  const handleToggleStar = async (id, starred) => {
    try {
      await axios.put(`/api/urls/${id}/starred`, { starred });
      await handleSearch(searchQuery, selectedTag, showStarredOnly);
    } catch (error) {
      console.error('Error toggling starred status:', error);
    }
  };

  const handleRenameTag = async (oldName, newName) => {
    try {
      await axios.put(`/api/tags/${encodeURIComponent(oldName)}`, { newName });
      // Update selected tag if it was renamed
      if (selectedTag === oldName) {
        setSelectedTag(newName);
      }
      await fetchUrls();
      await fetchTags();
      // Re-run search with new tag name if needed
      if (selectedTag === oldName) {
        handleSearch(searchQuery, newName, showStarredOnly);
      }
    } catch (error) {
      console.error('Error renaming tag:', error);
      alert(error.response?.data?.error || 'Failed to rename tag');
    }
  };

  const handleTagClick = useCallback((tagName) => {
    const newTag = selectedTag === tagName ? null : tagName;
    setSelectedTag(newTag);
    handleSearch(searchQuery, newTag, showStarredOnly);
  }, [selectedTag, searchQuery, showStarredOnly, handleSearch]);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    handleSearch(query, selectedTag, showStarredOnly);
  }, [selectedTag, showStarredOnly, handleSearch]);

  const handleStarredFilterChange = useCallback(() => {
    const newShowStarred = !showStarredOnly;
    setShowStarredOnly(newShowStarred);
    handleSearch(searchQuery, selectedTag, newShowStarred);
  }, [searchQuery, selectedTag, showStarredOnly, handleSearch]);

  if (!isAuthenticated) {
    return <Auth onLogin={handleLogin} />;
  }

  if (showTagManagement) {
    return (
      <div className="App">
        <TagManagement onBack={() => {
          setShowTagManagement(false);
          fetchUrls();
          fetchTags();
        }} />
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div>
            <h1>📚 My URL Bookmarks</h1>
            <p>Save, organize, and search your favorite links</p>
          </div>
          <div className="user-info">
            <button onClick={() => setShowTagManagement(true)} className="manage-tags-btn">
              🏷️ Manage Tags
            </button>
            <span>👤 {username}</span>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>
      
      <main className="App-main">
        <div className="container">
          <AddUrlForm onAdd={handleAddUrl} availableTags={tags} />
          
          <SearchBar onSearch={handleSearchChange} />
          
          <div className="filters-row">
            <TagFilter 
              tags={tags} 
              selectedTag={selectedTag}
              onTagClick={handleTagClick}
            />
            <button
              className={`starred-filter ${showStarredOnly ? 'active' : ''}`}
              onClick={handleStarredFilterChange}
            >
              {showStarredOnly ? '★ Starred only' : '☆ Starred only'}
            </button>
          </div>
          
          {loading ? (
            <div className="loading">Loading...</div>
          ) : urls.length === 0 ? (
            <div className="no-results">
              {searchQuery || selectedTag ? 
                'No URLs found matching your search.' : 
                'No URLs saved yet. Add your first bookmark above!'}
            </div>
          ) : (
            <div className="url-grid">
              {urls.map(url => (
                <UrlCard 
                  key={url.id} 
                  url={url}
                  onDelete={handleDeleteUrl}
                  onUpdateTags={handleUpdateTags}
                  onUpdateDescription={handleUpdateDescription}
                  onToggleStar={handleToggleStar}
                  availableTags={tags}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
